import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { TrendingDown, DollarSign, XCircle } from 'lucide-react';
interface SlideProblemProps {
  onContinue: () => void;
}
export function SlideProblem({
  onContinue
}: SlideProblemProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600);
    const t2 = setTimeout(() => setStage(2), 1600);
    const t3 = setTimeout(() => setStage(3), 2600);
    const t4 = setTimeout(() => setStage(4), 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-723" data-mp="2450o5l91ht12">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-4" data-id="element-724" data-mp="202vx8u9z7ai4">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-725" data-mp="hk7i7pss48fu">
          The Agency Bottleneck
        </h2>
        <p className="text-xl text-white/60" data-id="element-726" data-mp="mb7fwx9q2uje">
          Why legacy brands die earlier than they need to.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-4xl mx-auto w-full space-y-6" data-id="element-727" data-mp="8ku7yuwtzfu2">
        <div className="space-y-6" data-id="element-728" data-mp="cle2dqq0s5ci">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-729" data-mp="cii7ycddryst">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-730" data-mp="w8llg1u3xywx">
              <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-731" data-mp="3c09cxhqcqz8c" />
              The Cliff
            </h3>
            <p className="text-white/70" data-id="element-732" data-mp="3gysy3fx4g6zn">
              When patents expire, generic competition can cause sales to drop
              &gt;90% almost overnight. Marketing investment is stopped
              immediately.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          x: stage >= 2 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-733" data-mp="1hkj9kkfwiuwi">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-734" data-mp="13q08qdflvzgb">
              <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-735" data-mp="yvmhmn7z8ehg" />
              The Cost Trap
            </h3>
            <p className="text-white/70" data-id="element-736" data-mp="pslcopuuktfd">
              Traditional agency campaigns are too expensive to justify on
              declining revenue. The gap between revenue and cost approaches
              zero.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          x: stage >= 3 ? 0 : -20
        }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-6" data-id="element-737" data-mp="2k7xnvyxwrwi6">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-738" data-mp="fqrg2tdmfg9m">
              <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" data-id="element-739" data-mp="jpgp69rmmbng" />
              Premature Death
            </h3>
            <p className="text-white/90" data-id="element-740" data-mp="3o4gixyk94ed7">
              The real problem isn't that these brands lack value — it's that
              traditional marketing economics force companies to abandon them.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 10
      }} className="bg-navy-800/60 border border-navy-700 rounded-xl px-6 py-4 mt-2" data-id="element-741" data-mp="10jvo841zn6a">
          <p className="text-white/50 leading-relaxed text-sm" data-id="element-742" data-mp="1ayjoscn78hem">
            <span className="text-teal-400/80 font-medium" data-id="element-743" data-mp="15z6yfhx4cs7a">
              Note on biologics:
            </span>{' '}
            Biosimilar competition erodes revenue more gradually (15-30% per
            year), so marketing continues longer. But eventually the same
            dynamic applies — declining revenue meets fixed marketing costs, and
            attention fades. Both molecule and biologic brands face the same end
            state.
          </p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-744" data-mp="23m5xu0f74e8v"></motion.div>
    </div>;
}